// Ekran 08'in paylaşılan rozet/biçim yardımcıları.
// Eşlemeler ref_files/btys-erp-08-filo.html'den birebir.

import { SAAT_DILIMI, tr, trTarih } from '../../lib/trFormat'
import type {
  AracDurumu, BakimDurumu, CezaDurumu, DolumBandi, DorseUygunlugu, SeferDurumu, Vade,
} from './api'

/**
 * "gg.aa" — referansın kısa tarihi. Dar kaplar (panelin 40px'lik `.dt` sütunu,
 * yakıt tablosunun 96px'lik Tarih kolonu) yılı taşıyamıyor; biçim yine
 * `trTarih` üzerinden geliyor, ham `toLocaleDateString` ekrana girmiyor (§12).
 */
export const gunAy = (deger: string) => trTarih(deger).slice(0, 5)

/**
 * Gün adı ("Cumartesi") — sefer programı başlığı için.
 *
 * Referans HTML "15.08 Cuma" yazıyor ama 15.08.2026 bir CUMARTESİ; elle
 * yazılmış mock etiketi kopyalanmıyor, gün takvimden hesaplanıyor. Dilim
 * tesis saatinden (`SAAT_DILIMI`): UTC'ye göre hesaplansaydı gece yarısı
 * civarındaki bir tarih bir gün geri kayardı.
 *
 * Not: yeri normalde `lib/trFormat.ts`; bu turda o dosya dokunulmaz olduğu
 * için ekranın kendi yardımcı dosyasında duruyor.
 */
export function gunAdi(deger: string | Date | null | undefined): string {
  if (!deger) return ''
  const d = typeof deger === 'string' ? new Date(deger) : deger
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('tr-TR', { timeZone: SAAT_DILIMI, weekday: 'long' })
}

/** Referans: Bakımda → b-amber · Seferde/Sahada → stat-yolda · Hazır → b-navy. */
export function aracRozeti(durum: AracDurumu): [string, string] {
  switch (durum) {
    case 'bakimda': return ['badge b-amber', 'Bakımda']
    case 'seferde': return ['badge stat-yolda', 'Seferde']
    case 'sahada': return ['badge stat-yolda', 'Sahada']
    default: return ['badge b-navy', 'Hazır']
  }
}

export function dorseRozeti(u: DorseUygunlugu): [string, string] {
  return u === 'sivi' ? ['badge b-navy', 'Sıvı'] : ['badge stat-plan', 'Katı']
}

export function bakimRozeti(d: BakimDurumu): [string, string] {
  switch (d) {
    case 'serviste': return ['badge b-amber', 'Serviste']
    case 'planlandi': return ['badge stat-plan', 'Planlandı']
    default: return ['badge stat-tamam', 'Tamamlandı']
  }
}

/**
 * Referansta dört durum vardı; "İptal" listeye eklendi çünkü programdan çıkarma
 * düğmesi kaydı SİLMİYOR (K15 ruhu), seferi `iptal`e çekiyor — iptal edilmiş
 * satır select'te de doğru okunsun.
 */
export const SEFER_DURUMLARI: [SeferDurumu, string][] = [
  ['planlandi', 'Planlandı'],
  ['yolda', 'Yolda'],
  ['sahada', 'Sahada'],
  ['tamamlandi', 'Tamamlandı'],
  ['iptal', 'İptal'],
]

export const BAKIM_DURUMLARI: [BakimDurumu, string][] = [
  ['tamamlandi', 'Tamamlandı'],
  ['serviste', 'Serviste'],
  ['planlandi', 'Planlandı'],
]

/** Panelde referansın sırası farklı — ikisi de korunuyor. */
export const BAKIM_DURUMLARI_PANEL: [BakimDurumu, string][] = [
  ['planlandi', 'Planlandı'],
  ['serviste', 'Serviste'],
  ['tamamlandi', 'Tamamlandı'],
]

export const CEZA_DURUMLARI: [CezaDurumu, string][] = [
  ['bekliyor', 'Bekliyor'],
  ['odendi', 'Ödendi'],
  ['itiraz', 'İtiraz'],
]

export const seferEtiketi = (d: SeferDurumu) =>
  SEFER_DURUMLARI.find(([k]) => k === d)?.[1] ?? d

/** Panelde ceza durumu salt-okunur yazılıyor — tabloda select var. */
export const cezaEtiketi = (d: CezaDurumu) =>
  CEZA_DURUMLARI.find(([k]) => k === d)?.[1] ?? d

/** Sefer durumunun renkli noktası (referans satır 793). */
export const SEFER_NOKTASI: Record<SeferDurumu, string> = {
  planlandi: '#C7CFDA',
  yolda: 'var(--navy-500)',
  sahada: 'var(--amber-dot)',
  tamamlandi: '#2E7D46',
  iptal: 'var(--red)',
}

/**
 * Vade hücresi: sınıf + metin.
 *
 * Referans boş evrak listesinde "uygun" yazıyordu (Math.min() → Infinity);
 * evrağı olmayan varlık uygun DEĞİL, bilinmiyor.
 */
export function vadeHucresi(v: Vade, kisa = false): [string, string] {
  switch (v.durum) {
    case 'gecmis':
      return ['vd vd-r', kisa ? 'evrak geçti' : `${trTarih(v.tarih)} · geçti`]
    case 'yaklasiyor':
      return ['vd vd-a', kisa ? `${v.gun} gün` : `${trTarih(v.tarih)} · ${v.gun} gün`]
    case 'veriYok':
      return ['vd', 'veri yok']
    default:
      return ['vd', kisa ? 'uygun' : trTarih(v.tarih)]
  }
}

/** K37 sapma hücresi. Bant sunucudan geliyor (eşikler ayar_esik'ten). */
export function sapmaHucresi(sapma: number | null, bant: DolumBandi): [string, string] {
  if (sapma === null) return ['num vd', 'ilk dolum']
  const sinif = bant === 'kritik' ? 'num vd vd-r' : bant === 'izle' ? 'num vd vd-a' : 'num vd'
  return [sinif, `${sapma > 0 ? '+' : ''}${tr(sapma, 1)}%`]
}
